<script setup lang="ts">
import { computed } from 'vue';
import type { DayCode } from '@/types/availability';

const props = withDefaults(
	defineProps<{
		modelValue?: DayCode[];
		disabled?: boolean;
	}>(),
	{
		modelValue: () => [],
		disabled: false
	}
);

const emit = defineEmits<{
	'update:modelValue': [value: DayCode[]];
}>();
const selectedDaysProp = computed(() => props.modelValue ?? []);
const isDisabled = computed(() => props.disabled ?? false);

const days: Array<{ code: DayCode; label: string; fullLabel: string }> = [
	{ code: 'SUN', label: 'S', fullLabel: 'Sunday' },
	{ code: 'MON', label: 'M', fullLabel: 'Monday' },
	{ code: 'TUE', label: 'T', fullLabel: 'Tuesday' },
	{ code: 'WED', label: 'W', fullLabel: 'Wednesday' },
	{ code: 'THU', label: 'T', fullLabel: 'Thursday' },
	{ code: 'FRI', label: 'F', fullLabel: 'Friday' },
	{ code: 'SAT', label: 'S', fullLabel: 'Saturday' }
];

const selectedDays = computed({
	get: () => selectedDaysProp.value,
	set: (value) => emit('update:modelValue', value)
});

function toggleDay(code: DayCode) {
	if (isDisabled.value) return;

	const newValue = [...selectedDays.value];
	const index = newValue.indexOf(code);

	if (index === -1) {
		newValue.push(code);
	} else {
		newValue.splice(index, 1);
	}

	selectedDays.value = newValue;
}

function isSelected(code: DayCode) {
	return selectedDays.value.includes(code);
}

function selectWeekdays() {
	if (isDisabled.value) return;
	selectedDays.value = ['MON', 'TUE', 'WED', 'THU', 'FRI'];
}

function selectWeekends() {
	if (isDisabled.value) return;
	selectedDays.value = ['SAT', 'SUN'];
}

function selectAll() {
	if (isDisabled.value) return;
	selectedDays.value = days.map((d) => d.code);
}

function clearAll() {
	if (isDisabled.value) return;
	selectedDays.value = [];
}
</script>

<template>
	<div class="flex flex-col gap-2.5" :class="{ 'opacity-50 pointer-events-none': isDisabled }">
		<div class="flex gap-1.5">
			<button
				v-for="day in days"
				:key="day.code"
				type="button"
				class="flex items-center justify-center w-10 h-10 border-2 border-surface rounded-full bg-[var(--egs-content-background)] text-color-secondary text-sm font-semibold cursor-pointer transition-all hover:border-primary hover:text-primary hover:scale-105 disabled:cursor-not-allowed"
				:class="{ '!bg-primary !border-primary !text-white hover:!bg-primary-600 hover:!border-primary-600 hover:!text-white': isSelected(day.code) }"
				:disabled="isDisabled"
				:title="day.fullLabel"
				@click="toggleDay(day.code)"
			>
				{{ day.label }}
			</button>
		</div>
		<div class="flex gap-2 flex-wrap">
			<button
				type="button"
				class="py-1 px-2.5 border border-surface rounded-xl bg-[var(--egs-content-background)] text-color-secondary text-xs cursor-pointer transition-all hover:border-primary hover:text-primary hover:bg-primary/5 disabled:cursor-not-allowed"
				:disabled="isDisabled"
				@click="selectWeekdays"
			>
				Weekdays
			</button>
			<button
				type="button"
				class="py-1 px-2.5 border border-surface rounded-xl bg-[var(--egs-content-background)] text-color-secondary text-xs cursor-pointer transition-all hover:border-primary hover:text-primary hover:bg-primary/5 disabled:cursor-not-allowed"
				:disabled="isDisabled"
				@click="selectWeekends"
			>
				Weekends
			</button>
			<button
				type="button"
				class="py-1 px-2.5 border border-surface rounded-xl bg-[var(--egs-content-background)] text-color-secondary text-xs cursor-pointer transition-all hover:border-primary hover:text-primary hover:bg-primary/5 disabled:cursor-not-allowed"
				:disabled="isDisabled"
				@click="selectAll"
			>
				All
			</button>
			<button
				type="button"
				class="py-1 px-2.5 border border-surface rounded-xl bg-[var(--egs-content-background)] text-color-secondary text-xs cursor-pointer transition-all hover:border-primary hover:text-primary hover:bg-primary/5 disabled:cursor-not-allowed"
				:disabled="isDisabled"
				@click="clearAll"
			>
				Clear
			</button>
		</div>
	</div>
</template>
